import type { Campaign } from "./types";

// docs/plans/2026-10-13-campaigns.md. Shared by the config sync (normalizing Sheet cells), the
// Session API (resolving a campaign at session start) and the embed's mock preview, so path and
// expiry rules can't drift between them.

/** What a session carries once a campaign has been resolved for it. Frozen at session start, so a
 * campaign expiring or being edited mid-session doesn't change what the visitor was shown. */
export type SessionCampaign = Pick<Campaign, "id" | "description" | "code">;

/** Campaign expiry is a calendar day for a US business (High Point is Eastern), not a UTC instant. */
const CAMPAIGN_TIME_ZONE = "America/New_York";

/** Google Sheets' day zero. With `UNFORMATTED_VALUE` (src/config/sheets.ts), a cell Sheets
 * recognizes as a date arrives as a day count from here rather than as the text shown in the cell. */
const SHEETS_EPOCH_MS = Date.UTC(1899, 11, 30);
const DAY_MS = 24 * 60 * 60 * 1000;

/** Lowercase, no query string or fragment, no trailing slash (except the root itself). A full URL
 * pasted into the Sheet is reduced to its path, so `https://site.com/Start/` and `/start` match. */
export function normalizeUrlPath(raw: string): string {
  let path = raw.trim();
  if (/^https?:\/\//i.test(path)) {
    try {
      path = new URL(path).pathname;
    } catch {
      // Left as typed; validation reports it as not starting with "/".
    }
  }
  path = path.replace(/[?#].*$/, "").toLowerCase();
  // Every trailing slash goes, so the root collapses to "" and is put back.
  const startsWithSlash = path.startsWith("/");
  path = path.replace(/\/+$/, "");
  return path === "" && startsWithSlash ? "/" : path;
}

/** Accepts a Sheets date serial, `YYYY-MM-DD`, or US `M/D/YYYY`, returning `YYYY-MM-DD`. Anything
 * else is returned trimmed but otherwise as typed, for validation to reject by name. */
export function normalizeExpiry(raw: string): string {
  const value = raw.trim();
  if (/^\d+(\.\d+)?$/.test(value)) {
    return new Date(SHEETS_EPOCH_MS + Math.floor(Number(value)) * DAY_MS).toISOString().slice(0, 10);
  }
  const us = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (us) return `${us[3]}-${us[1]!.padStart(2, "0")}-${us[2]!.padStart(2, "0")}`;
  return value;
}

/** True for a real calendar date in `YYYY-MM-DD` form (rejects 2026-02-30). */
export function isValidIsoDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const d = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === value;
}

/** Today's date in US Eastern time, as `YYYY-MM-DD`. */
export function campaignToday(now: Date): string {
  // en-CA formats as YYYY-MM-DD.
  return new Intl.DateTimeFormat("en-CA", { timeZone: CAMPAIGN_TIME_ZONE, year: "numeric", month: "2-digit", day: "2-digit" }).format(now);
}

/** Live through the end of its `expires` day, Eastern. */
export function isCampaignLive(campaign: Pick<Campaign, "expires">, now: Date): boolean {
  return campaignToday(now) <= campaign.expires;
}

/** The live campaign for the page the embed was opened on, if any. */
export function resolveCampaign(campaigns: Campaign[] | undefined, pagePath: string | undefined, now: Date): SessionCampaign | undefined {
  if (!campaigns?.length || !pagePath) return undefined;
  const path = normalizeUrlPath(pagePath);
  const match = campaigns.find((c) => c.urlPath === path && isCampaignLive(c, now));
  return match ? { id: match.id, description: match.description, code: match.code } : undefined;
}
